import { gql } from '@apollo/client';

export const IS_CHANNEL_HANDLE_AVAILABLE = gql`
  query IsChannelHandleAvailable($chHandle: String!) {
    isChannelHandleAvailable(chHandle: $chHandle)
  }
`;

export type IsChannelHandleAvailableData = {
  isChannelHandleAvailable: boolean;
};

export type IsChannelHandleAvailableVariables = {
  chHandle: string;
};

export const SUGGEST_CHANNEL_HANDLES = gql`
  query suggestChannelHandles($chHandle: String!) {
    suggestChannelHandles(chHandle: $chHandle)
  }
`;

export type SuggestChannelHandlesData = {
  suggestChannelHandles: string[] | string;
};

export type SuggestChannelHandlesVariables = {
  chHandle: string;
};

export const IS_CHANNEL_AVAILABLE = gql`
  query IsChannelAvailable {
    isChannelAvailable
  }
`;

export type IsChannelAvailableData = {
  isChannelAvailable: boolean;
};

export type Channel = {
  chId: string;
  chName: string;
  chHandle: string;
  chDescription: string;
  chAvatarUrl: string;
  chBannerUrl: string;
  chCreatedAt: string;
  chUpdatedAt: string;
  videosCount: number;
  followingCount: number;
  followersCount: number;
  isReported: boolean;
  userInfo: {
    uId: string;
    uEmailId: string;
    uAvatarUrl: string;
    uFullName: string;
  };
};

export const GET_MY_CHANNEL = gql`
  query GetMyChannel($chId: String) {
    getMyChannel(chId: $chId) {
      channels {
        chId
        chName
        chHandle
        chDescription
        chAvatarUrl
        chBannerUrl
        chCreatedAt
        chUpdatedAt
        videosCount
        followingCount
        followersCount
        isReported
        userInfo {
          uId
          uEmailId
          uAvatarUrl
          uFullName
        }
      }
      total
    }
  }
`;

export type GetMyChannelData = {
  getMyChannel: {
    channels: Channel[];
    total: number;
  };
};



// MY VIDEOS
export const GET_CHANNEL_VIDEOS = gql`
  query getMyVideos($input: GetMyVideosInput!) {
    getMyVideos(input: $input) {
      totalCount
      videos {
        playlistId
        isReported
        maId
        maTitle
        maDescription
        maCategory
        maType
        maJwMediaId
        maJwMediaUrl
        maJwThumbNailUrl
        maJwMediaUploadStatus
        maJwMediaInfo
        maStatus
        maTags
        maDuration
        maS3UploadId
        maJwThumbnailId
        maS3ThumbnailKeyUrl
        maS3MediaKeyUrl
        maAdditionalImages
        maUploadedBy
        maUploadedAt
        maApprovedBy
        maApprovedAt
        maRejectedBy
        maRejectedAt
        maRejectionReason
        maUploadStatus
        maCreatedAt
        maUpdatedAt
        maDeletedAt
        ageRatingId
        uploadedUserInfo {
          uId
          uEmailId
          uFullName
          uAvatarUrl
        }
        channelInfo {
          chId
          chName
          chHandle
          chAvatarUrl
          chBannerUrl
          isFollowedByMe
          followersCount
        }
        ageRatingInfo {
          ddId
          ddLabel
          ddValue
        }
        likeCount
        isLikedByUser
        genres {
          ddId
          ddLabel
          ddValue
        }
        maCategoryId
        maOriginalFileName
        maFileSize
        categoryInfo {
          ddId
          ddLabel
          ddValue
        }
      }
    }
  }
`;


/* ---------- Types ---------- */

export type ChannelInfo = {
  chId: string;
  chName: string;
  chHandle: string;
  chAvatarUrl: string | null;
  chBannerUrl: string | null;
  isFollowedByMe?: boolean | null;
  followersCount?: number | null;
};

export type ChannelVideoUser = {
  uId: string;
  uEmailId: string;
  uFullName: string;
  uAvatarUrl: string | null;
};

export type ChannelVideoDropdown = {
  ddId: string;
  ddLabel: string;
  ddValue: string;
};

export type ChannelVideo = {
  playlistId: string | null;
  isReported: boolean | null;  // ← Add this
  maId: string;
  maTitle: string;
  maDescription: string | null;
  maCategory: string | null;
  maType: 'REELS' | 'VIDEOS' | string;
  maJwMediaId: string | null;
  maJwMediaUrl: string | null;
  maJwThumbNailUrl: string | null;
  maJwMediaUploadStatus: string | null;
  maJwMediaInfo: unknown;
  maStatus: string;
  maTags: string[] | string | null;
  maDuration: string | number | null;
  maS3UploadId: string | null;
  maJwThumbnailId: string | null;
  maS3ThumbnailKeyUrl: string | null;
  maS3MediaKeyUrl: string | null;
  maAdditionalImages: unknown;
  maUploadedBy: string | null;
  maUploadedAt: string | null;
  maApprovedBy: string | null;
  maApprovedAt: string | null;
  maRejectedBy: string | null;
  maRejectedAt: string | null;
  maRejectionReason: string | null;
  maUploadStatus: string;
  maCreatedAt: string;
  maUpdatedAt: string;
  maDeletedAt: string | null;
  ageRatingId: string | null;
  uploadedUserInfo: ChannelVideoUser | null;
  channelInfo: ChannelInfo | null;  // ← Add this
  ageRatingInfo: ChannelVideoDropdown | null;
  likeCount: number | null;
  isLikedByUser: boolean | null;
  isFollowing?: boolean | null;
  genres: ChannelVideoDropdown[] | null;
  maCategoryId: string | null;
  maOriginalFileName: string | null;
  maFileSize: number | null;
  categoryInfo: ChannelVideoDropdown | null;
};

export type GetChannelVideosData = {
  getMyVideos: {
    totalCount: number;
    videos: ChannelVideo[];
  };
};

export type GetChannelVideosVariables = {
  input: {
    first: number;
    offset: number;
    maType?: 'reels' | 'videos';
    maUploadStatus: string;
    chId: string[];
    search?: string;
  };
};

/* ---------- Channel Home ---------- */

export const GET_CHANNEL_HOME = gql`
  query GetChannelHome($input: GetChannelHomeInput!) {
    getChannelHome(input: $input) {
      reels {
        maId
        maTitle
        maDescription
        maCategory
        maCategoryId
        maOriginalFileName
        maFileSize
        maType
        maJwMediaId
        maJwMediaUrl
        maJwThumbNailUrl
        maJwMediaUploadStatus
        maJwMediaInfo
        maStatus
        maTags
        maDuration
        maS3UploadId
        maJwThumbnailId
        maS3ThumbnailKeyUrl
        maS3MediaKeyUrl
        maAdditionalImages
        maUploadedBy
        maUploadedAt
        maApprovedBy
        maApprovedAt
        maRejectedBy
        maRejectedAt
        maRejectionReason
        maUploadStatus
        maCreatedAt
        maUpdatedAt
        maDeletedAt
        ageRatingId
        isReported
        uploadedUserInfo {
          uId
          uEmailId
          uFullName
          uAvatarUrl
        }
        categoryInfo {
          ddId
          ddLabel
          ddValue
          ddType
        }
        playlistId
      }
      videos {
        maId
        maTitle
        maDescription
        maCategory
        maCategoryId
        maOriginalFileName
        maFileSize
        maType
        maJwMediaId
        maJwMediaUrl
        maJwThumbNailUrl
        maJwMediaUploadStatus
        maJwMediaInfo
        maStatus
        maTags
        maDuration
        maS3UploadId
        maJwThumbnailId
        maS3ThumbnailKeyUrl
        maS3MediaKeyUrl
        maAdditionalImages
        maUploadedBy
        maUploadedAt
        maApprovedBy
        maApprovedAt
        maRejectedBy
        maRejectedAt
        maRejectionReason
        maUploadStatus
        maCreatedAt
        maUpdatedAt
        maDeletedAt
        ageRatingId
        uploadedUserInfo {
          uId
          uEmailId
          uFullName
          uAvatarUrl
        }
        categoryInfo {
          ddId
          ddLabel
          ddValue
          ddType
        }
        playlistId
      }
    }
  }
`;

export type ChannelHomeCategoryInfo = ChannelVideoDropdown & {
  ddType?: string | null;
};

export type ChannelHomeMedia = {
  playlistId: string | null;
  maId: string;
  maTitle: string;
  maDescription: string | null;
  maCategory: string | null;
  maCategoryId: string | null;
  maOriginalFileName: string | null;
  maFileSize: number | null;
  maType: 'REELS' | 'VIDEOS' | 'reels' | 'videos' | string;
  maJwMediaId: string | null;
  maJwMediaUrl: string | null;
  maJwThumbNailUrl: string | null;
  maJwMediaUploadStatus: string | null;
  maJwMediaInfo: unknown;
  maStatus: string;
  maTags: string[] | string | null;
  maDuration: string | number | null;
  maS3UploadId: string | null;
  maJwThumbnailId: string | null;
  maS3ThumbnailKeyUrl: string | null;
  maS3MediaKeyUrl: string | null;
  maAdditionalImages: unknown;
  maUploadedBy: string | null;
  maUploadedAt: string | null;
  maApprovedBy: string | null;
  maApprovedAt: string | null;
  maRejectedBy: string | null;
  maRejectedAt: string | null;
  maRejectionReason: string | null;
  maUploadStatus: string;
  maCreatedAt: string;
  maUpdatedAt: string;
  maDeletedAt: string | null;
  ageRatingId: string | null;
  uploadedUserInfo: ChannelVideoUser | null;
  categoryInfo: ChannelHomeCategoryInfo | null;
  isReported?: boolean | null;
  isFollowing?: boolean | null;
};

export type GetChannelHomeData = {
  getChannelHome: {
    reels: ChannelHomeMedia[];
    videos: ChannelHomeMedia[];
  };
};

export type GetChannelHomeVariables = {
  input: {
    limit: number;
    offset: number;
    maUploadStatus: string;
    chId: string;
  };
};

export type ChannelConnectionType = 'FOLLOWING' | 'FOLLOWERS';

export type ChannelConnectionUser = {
  uId: string;
  uEmailId: string;
  uFullName: string;
  uAvatarUrl: string | null;
};

export type ChannelConnectionItem = {
  chId: string;
  chName: string | null;
  chHandle: string | null;
  chDescription: string | null;
  chAvatarUrl: string | null;
  chBannerUrl: string | null;
  chCreatedAt: string | null;
  chUpdatedAt: string | null;
  userInfo: ChannelConnectionUser | null;
  videosCount: number | null;
  followersCount: number | null;
  followingCount: number | null;
  isFollowedByMe: boolean | null;
  isReported: boolean | null;
};

export type GetChannelConnectionsInput = {
  chId: string;
  limit: number;
  offset: number;
  search?: string;
  type: ChannelConnectionType;
};

export type GetChannelConnectionsData = {
  getChannelConnections: {
    items: ChannelConnectionItem[];
    totalCount: number;
    followingCount: number;
    followersCount: number;
  };
};

export type GetChannelConnectionsVariables = {
  input: GetChannelConnectionsInput;
};

export const GET_CHANNEL_CONNECTIONS = gql`
  query GetChannelConnections($input: GetChannelConnectionsInput!) {
    getChannelConnections(input: $input) {
      items {
        chId
        chName
        chHandle
        chDescription
        chAvatarUrl
        chBannerUrl
        chCreatedAt
        chUpdatedAt
        userInfo {
          uId
          uEmailId
          uFullName
          uAvatarUrl
        }
        videosCount
        followersCount
        followingCount
        isFollowedByMe
        isReported
      }
      totalCount
      followingCount
      followersCount
    }
  }
`;